import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import NotFound from '../../pages/system/NotFound';
import { AppDispatch, RootState } from '../../redux-stores/store';
import { fetchDMAutomation } from '../../redux-stores/slices/dmAutomationSlice';
import withModal from '../../utils/hoc/withModal';
import PostSkeleton from '../../skeletons/PostSkeleton';
import { POSTAPIError } from '../../types';
import LinkPostFields from './LinkPostFields';
import { LinkDataType } from './AddLink';
import { ERROR } from '../../constants/errors';
import { deleteDMAutomation, updateDMAutomation } from '../../services/api/dmAutomations';
import { toast } from 'react-toastify';
import { fetchDMAutomations } from '../../redux-stores/slices/dmAutomationsListSlice';
import { copyToClipboard } from '../../utils/helpers';

interface AutomationInsightsProps {
    postId: string | null,
    onClose: () => void,
    onError: (error: POSTAPIError) => void
}

const AutomationInsights: React.FC<AutomationInsightsProps> = ({ postId, onClose, onError }) => {

    const dispatch: AppDispatch = useDispatch();

    const dmAutomation = useSelector((state: RootState) => state.dmAutomations.items[postId!]?.dmAutomation ?? null);

    const loading = useSelector((state: RootState) => state.dmAutomations.loading);

    const [editMode, setEditMode] = React.useState<boolean>(false);
    const [postError, setPOSTError] = React.useState<POSTAPIError | null>(null);

    const [isProcessing, setProcessing] = React.useState(false);

    const [deleteToast, setDeleteToast] = useState<string | number | null>(null);

    const selectedSocialAccount = useSelector((state: RootState) => state.dmAutomationsList.selectedSocialAccount);

    const [link, setLink] = useState<LinkDataType>({
        post: dmAutomation?.automation?.social_post.uuid ?? null,
        triggers: dmAutomation?.automation?.triggers ?? [],
        url: dmAutomation?.automation?.url ?? '',
        button_text: dmAutomation?.automation?.button_text ?? '',
        historical: dmAutomation?.automation?.historical ?? false,
        active: dmAutomation?.automation?.active ?? true
    });


    useEffect(() => {
        const setDefaults = () => {
            setLink({
                post: dmAutomation?.automation?.social_post.uuid ?? null,
                triggers: dmAutomation?.automation?.triggers ?? [],
                url: dmAutomation?.automation?.url ?? '',
                button_text: dmAutomation?.automation?.button_text ?? '',
                historical: dmAutomation?.automation?.historical ?? false,
                active: dmAutomation?.automation?.active ?? true
            })
            setPOSTError(null);
            setProcessing(false);
        }
        setDefaults();
    }, [dmAutomation, editMode])

    const fetchDMAutomationData = useCallback(async () => {
        if (!postId) return;
        dispatch(fetchDMAutomation({ postId }));
        setEditMode(false);
    }, [postId, dispatch])

    useEffect(() => {
        fetchDMAutomationData()
    }, [postId, fetchDMAutomationData])

    const updateLink = async () => {
        try {
            setProcessing(true);
            await updateDMAutomation(postId!, { button_text: link.button_text, triggers: link.triggers });
            toast.success("Smartlink updated successfully!");
            dispatch(fetchDMAutomation({ postId: postId!, force: true }));
            if(selectedSocialAccount){
                dispatch(fetchDMAutomations({ socialAccountId: selectedSocialAccount?.social_account?.uuid, page: 1, force: true }))
            }
        } catch (error: any) {
            if (error.response.data) {
                setPOSTError(error.response.data);
            }
        } finally {
            setEditMode(false)
        }
    }

    const removeLink = async (toastId: string | number) => {
        try {
            if(dmAutomation.automation.status === 'inactive') return;
            setProcessing(true);
            await deleteDMAutomation(postId!);
            toast.success("Smartlink removed successfully!");
            dispatch(fetchDMAutomation({ postId: postId!, force: true }));
            if(selectedSocialAccount){
                dispatch(fetchDMAutomations({ socialAccountId: selectedSocialAccount?.social_account?.uuid, page: 1, force: true }))
            }
            onClose();
        } catch (error: any) {
            if (error.response.data) {
                setPOSTError(error.response.data);
            }
        } finally {
            dismissDeletionToast(toastId);
        }
    }

    const dismissDeletionToast = (toastId: string | number) => {
        toast.dismiss(toastId);
        setDeleteToast(null);
    }

    const handleDelete = () => {
        if (deleteToast || dmAutomation.automation.status === 'inactive') return;

        const toastId = toast.warning(
            <div className='d-flex justify-content-between align-items-center w-100'>
                <span className='font-12'>Are you sure you want to remove this link?</span>
                <div className='d-flex gap-2'>
                    <button className='toaster-confirm-btn' onClick={() => removeLink(toastId)}>Yes</button>
                    <button className='toaster-decline-btn' onClick={() => dismissDeletionToast(toastId)}>No</button>
                </div>
            </div>,
            { autoClose: false, closeOnClick: false, position: "bottom-center", closeButton: false }
        );

        setDeleteToast(toastId);
    };

    const onPostError = useCallback(async () => {
        if (postError && postError.error_code === ERROR.INSUFFICIENT_PERMISSIONS) {
            onError(postError);
        }
    }, [postError, onError])

    useEffect(() => {
        onPostError();
    }, [postError, onPostError])


    if (!postId) return <NotFound />

    if (loading) return <PostSkeleton />

    return (
        <div>
            {
                dmAutomation &&
                <React.Fragment>
                    <div className='position-relative'>
                        <img src={dmAutomation.automation.social_post.thumbnail_url} alt="post" className='post' />
                        {
                            dmAutomation.automation.status === 'active' &&
                            <div className='position-absolute linked-badge text-success'>
                                Linked
                            </div>
                        }
                        {
                            dmAutomation.automation.status === 'inactive' &&
                            <div className='position-absolute inactive-badge text-danger'>
                                Inactive
                            </div>
                        }
                        {
                            dmAutomation.automation.status === 'active' &&
                            <div className="post-action rounded-4">
                                <span className='cursor-pointer mb-2' onClick={handleDelete}><i className="fa-solid fa-trash me-2"></i>Remove Link</span>
                            </div>
                        }

                    </div>
                    <div className="content m-0 p-3 bg-modal pb-4">
                        <div className="card-body text-start">
                            <span className="d-block text-muted font-10">{dmAutomation.automation.social_post.posted_on}</span>
                            {dmAutomation.automation.social_post.ig?.caption && <p style={{ height: '50px' }}>{dmAutomation.automation.social_post.ig?.caption?.slice(0, 100)}{(dmAutomation.automation.social_post.ig?.caption?.length ?? 0) > 100 ? '...' : ''}</p>}
                        </div>
                        <div className="d-flex mt-2 gap-2">
                            <button className="btn btn-primary-outlined rounded-sm btn-sm w-100" onClick={() => copyToClipboard(dmAutomation.automation.url, "Link copied to your clipboard!")}>
                                <i className="fa-solid fa-copy me-2"></i> Copy Link
                            </button>
                        </div>
                        {
                            editMode ? (
                                <div className='mt-3 mb-1'>
                                    <LinkPostFields
                                        link={link}
                                        setLink={setLink}
                                        disableSubmission={isProcessing}
                                        onCancel={() => setEditMode(false)}
                                        onSubmit={updateLink}
                                        editMode={true}
                                        viewOnly={dmAutomation.automation.status === 'inactive'}
                                        post={dmAutomation.automation.social_post}
                                    />
                                </div>) : (
                                <div className="insights my-3">
                                    <h4 className="pop mb-2">Insights</h4>
                                    <div className="d-flex gap-2 mb-2">
                                        <div className="card card-style m-0 shadow-none p-3 w-100">
                                            <span className="pop font-16">Sent</span>
                                            <h1 className='font-34 mt-3'>{dmAutomation.stats.sent}</h1>
                                        </div>
                                        <div className="card card-style m-0 shadow-none p-3 w-100">
                                            <span className="pop font-16">Clicks</span>
                                            <h1 className='font-34 mt-3'>{dmAutomation.stats.clicks}</h1>
                                        </div>
                                    </div>
                                    <div className="card card-style m-0 shadow-none p-3 w-100">
                                        <span className="pop font-16">CTR</span>
                                        <h1 className='font-34 mt-3'>{dmAutomation.stats.ctr}%</h1>
                                    </div>
                                </div>)
                        }

                    </div>
                </React.Fragment>
            }
        </div>
    )
}

export default withModal(AutomationInsights)